import WebView, { WebViewMessageEvent } from 'react-native-webview'
import { useState, useEffect } from 'react'
import { Platform } from 'react-native'
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake'
import { getToken } from 'tamagui'
import { IUpdateLessonProgress } from '@components/MediaRender'

interface IVimeoVideoPlayerProps {
  videoId: string
  updateProgress: (params: IUpdateLessonProgress) => void
  videoStartTime?: number
  actualProgress?: number
  domain: string
}

interface IVimeoVideoPlayerData {
  currentTime: number
  duration: number
  percent: number
}

interface IVimeoEventData {
  event: 'timeupdate' | 'play' | 'pause'
  data: IVimeoVideoPlayerData
}

interface IVimeoVideoPlayerCallbacks {
  timeupdate: (data: IVimeoVideoPlayerData) => void
  play: () => void
  pause: () => void
}

const thresholds = [25, 50, 75, 100] as const

export function VimeoVideoPlayer({
  videoId,
  updateProgress,
  videoStartTime,
  actualProgress = 0,
  domain,
}: IVimeoVideoPlayerProps) {
  const [progressMarkers, setProgressMarkers] = useState({
    25: actualProgress >= 25,
    50: actualProgress >= 50,
    75: actualProgress >= 75,
    100: actualProgress >= 100,
  })
  const [isPlaying, setIsPlaying] = useState(false)
  const videoBorderRadius = getToken('$border-radius-md')

  const isAndroid = Platform.OS === 'android'

  useEffect(() => {
    if (isPlaying && isAndroid) {
      activateKeepAwakeAsync()
    } else {
      deactivateKeepAwake()
    }

    return () => {
      deactivateKeepAwake()
    }
  }, [isPlaying, isAndroid])

  const videoCallbacks: IVimeoVideoPlayerCallbacks = {
    timeupdate: (data) => {
      thresholds.forEach((threshold) => {
        if (data.percent >= threshold && !progressMarkers[threshold]) {
          setProgressMarkers((prev) => ({ ...prev, [threshold]: true }))
          updateProgress?.({
            progress: threshold,
            currentVideoTime: Math.floor(data.currentTime),
          })
        }
      })
    },
    play: () => {
      setIsPlaying(true)
    },
    pause: () => {
      setIsPlaying(false)
    },
  }

  const playerUrl = `https://player.vimeo.com/video/${videoId}?api=1&autoplay=0&playsinline=1&start=${videoStartTime}`

  const handleMessage = (event: WebViewMessageEvent) => {
    try {
      const data: IVimeoEventData = JSON.parse(event.nativeEvent.data)
      if (data.event === 'timeupdate') {
        videoCallbacks.timeupdate(data.data)
      } else if (data.event === 'play') {
        videoCallbacks.play()
      } else if (data.event === 'pause') {
        videoCallbacks.pause()
      }
    } catch (error) {
      console.error('Error parsing message from Vimeo player:', error)
    }
  }

  const injectedJavaScript = `
    var player = new Vimeo.Player(document.querySelector('iframe'));
    player.on('timeupdate', function(data) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ event: 'timeupdate', data: data }));
    });
    player.on('play', function() {
      window.ReactNativeWebView.postMessage(JSON.stringify({ event: 'play', data: {} }));
    });
    player.on('pause', function() {
      window.ReactNativeWebView.postMessage(JSON.stringify({ event: 'pause', data: {} }));
    });
    true;
  `

  return (
    <WebView
      source={{
        uri: playerUrl,
        headers: {
          Referer: domain,
        },
      }}
      allowsInlineMediaPlayback
      javaScriptEnabled
      injectedJavaScript={injectedJavaScript}
      onMessage={handleMessage}
      style={{
        borderRadius: videoBorderRadius,
        backgroundColor: 'transparent',
      }}
    />
  )
}
